import { z } from "zod";

/**
 * Предмет, который ведёт преподаватель и которому посвящён урок.
 *
 * Закрытый список. Немецкий в нём есть, хотя предметного слоя (генераторов,
 * карты тем) у него нет: преподавателю немецкого достаются только
 * предметно-нейтральные и языковые инструменты. Пары «преподаватель и ученик»
 * держат своё поле (`StudentSubject`), и оно собрано из этого списка плюс
 * «другое», чтобы два списка не разошлись.
 */
export const Subject = z.enum(["math", "english", "russian", "german"]);
export type Subject = z.infer<typeof Subject>;

/** Все предметы в порядке показа. Этим же порядком хранится список в базе. */
export const ALL_SUBJECTS: readonly Subject[] = Subject.options;

export const SUBJECT_TITLES: Record<Subject, string> = {
  math: "Математика",
  english: "Английский",
  russian: "Русский",
  german: "Немецкий",
};

/** Языковые предметы: у них есть пропуски в тексте и сборка предложения. */
export const LANGUAGE_SUBJECTS: readonly Subject[] = ["english", "russian", "german"];

/**
 * Предметы преподавателя в порядке `ALL_SUBJECTS`, без повторов.
 *
 * ⚠️ `null` и `undefined` означают ВСЕ предметы, а не «ни одного». Колонка
 * `app_user.subjects` пуста у всех, кого не настроили руками, и у старого API,
 * который поля не присылает: в обоих случаях человек обязан видеть всё, как
 * до появления списка. Пустой массив читается так же: сервер его не хранит
 * (CHECK), а спрятать от человека все инструменты значило бы сломать кабинет.
 *
 * ⚠️ Принимает строки, а не `Subject`: `MeResponse.subjects` разбирается слабо
 * ради совместимости вперёд, и незнакомый бандлу предмет отбрасывается здесь.
 * Если после отбора не осталось ничего, это тоже «все», по той же причине.
 */
export function teacherSubjects(list: readonly string[] | null | undefined): Subject[] {
  if (list === null || list === undefined) return [...ALL_SUBJECTS];
  const known = ALL_SUBJECTS.filter((s) => list.includes(s));
  return known.length === 0 ? [...ALL_SUBJECTS] : known;
}

/**
 * Настраивал ли человек свой список вообще: есть ли в сыром значении хоть один
 * знакомый предмет. `null`, отсутствие поля и список из одних незнакомых
 * значений это «не настраивал», и тогда ничего не прячется.
 */
export function hasSubjectList(list: readonly string[] | null | undefined): boolean {
  return list !== null && list !== undefined && list.some((s) => Subject.safeParse(s).success);
}

/** Тело `PUT /v1/me/subjects`. Порядок и повторы нормализует сервер. */
export const UpdateSubjectsRequest = z.object({
  subjects: z.array(Subject).min(1).max(ALL_SUBJECTS.length * 2),
});
export type UpdateSubjectsRequest = z.infer<typeof UpdateSubjectsRequest>;

/** Тело `PUT /v1/lessons/:id/subject`. */
export const UpdateLessonSubjectRequest = z.object({ subject: Subject });
export type UpdateLessonSubjectRequest = z.infer<typeof UpdateLessonSubjectRequest>;

/**
 * Предмет урока, которым решается, какие инструменты показать в комнате.
 *
 * Порядок: выбранный на уроке, если он есть в списке преподавателя; иначе
 * предмет пары, если это предмет из списка (у пары бывает «другое»); иначе
 * первый предмет преподавателя. Выбор, сделанный до того, как преподаватель
 * убрал предмет из своего списка, поэтому не держит его в чужом предмете.
 *
 * `teacherSubjects` ожидается уже разрешённым (`teacherSubjects(me.subjects)`),
 * но пустой список тоже переживается: тогда это все предметы.
 */
export function resolveLessonSubject(args: {
  /** Строкой, а не `Subject`: ответ урока несёт поле слабой схемой (`Lesson.subject`). */
  lessonSubject: string | null | undefined;
  teacherSubjects: readonly Subject[];
  pairSubject: string | null | undefined;
}): Subject {
  const list = teacherSubjects(args.teacherSubjects);
  const lesson = Subject.safeParse(args.lessonSubject);
  if (lesson.success && list.includes(lesson.data)) return lesson.data;
  const pair = Subject.safeParse(args.pairSubject);
  if (pair.success && list.includes(pair.data)) return pair.data;
  // Список после `teacherSubjects` не бывает пустым, запасное значение ради типа
  return list[0] ?? "math";
}
